import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { RejectedReason } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { formatRateWithType } from '~utils/rateFormatting';

import { CHARGES_CELL_SX, NO_VALUE_PLACEHOLDER } from '../chargesGrid';
import RejectedChip from './RejectedChip';

interface ChargeAmountCellProps {
  addOnsAmount?: number;
  amount: number | null;
  isRejected?: boolean;
  rate?: number | null;
  rateType?: string | null;
  rejectedReason?: RejectedReason | null;
  sx?: SxProps<Theme>;
  wasRate?: number | null;
}

export default function ChargeAmountCell({
  addOnsAmount = 0,
  amount,
  isRejected = false,
  rate = null,
  rateType = null,
  rejectedReason = null,
  sx,
  wasRate = null,
}: ChargeAmountCellProps) {
  const { currencyFormatter } = useCompanyCurrency();

  return (
    <Box
      data-test-id="charge-amount-cell"
      sx={[CHARGES_CELL_SX, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <OverflowAwareText
        sx={
          isRejected
            ? (theme) => ({
                color: theme.palette.text.disabled,
                fontWeight: 400,
                textDecoration: 'line-through',
              })
            : { fontWeight: 700 }
        }
      >
        {amount === null ? NO_VALUE_PLACEHOLDER : currencyFormatter(amount)}
      </OverflowAwareText>

      {isRejected && <RejectedChip reason={rejectedReason} />}

      {!isRejected && rate !== null && (
        <OverflowAwareText>
          {formatRateWithType(rate, rateType, currencyFormatter)}
        </OverflowAwareText>
      )}

      {!isRejected && wasRate !== null && (
        <OverflowAwareText
          data-test-id="charge-was-rate"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray2 })}
        >
          {t('approvals.approve_charges.was', {
            amount: formatRateWithType(wasRate, rateType, currencyFormatter),
          })}
        </OverflowAwareText>
      )}

      {!isRejected && addOnsAmount > 0 && (
        <OverflowAwareText sx={{ textDecoration: 'underline dotted' }}>
          {t('approvals.approve_charges.add_ons_amount', {
            amount: currencyFormatter(addOnsAmount),
          })}
        </OverflowAwareText>
      )}
    </Box>
  );
}
